import type { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../auth/AdminAuthContext';
import { Logo } from './ui/Logo';

interface AdminLayoutProps {
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}

// NAV_ITEMS の active 判定ルール:
// - end: true   → 完全一致のみ(子ルートでは active にしない)
//                例: 「セッション履歴」(`/admin/sessions`)は
//                `/admin/sessions/active` のとき active にしない
// - end: false  → 完全一致 + 子ルート(`/<to>/...`) でも active
// - excludePrefix → 指定 prefix に該当するパスは active から除外する。
//   「パフォーマー」(`/admin/performers`)は `/admin/performers/pending`
//   が「承認待ち」専用なので、それ以外の子ルート(個別詳細
//   `/admin/performers/01HXXX...`)だけ active にする。
//
// React Router の NavLink は path prefix match で active 判定するため
// 何も指定しないと「承認待ち」クリックで「パフォーマー」も同時に active
// になるバグが出る(本ファイル冒頭の修正の主目的)。
export interface AdminNavItem {
  to: string;
  label: string;
  end: boolean;
  excludePrefix?: string;
}

export const NAV_ITEMS: AdminNavItem[] = [
  { to: '/admin/dashboard', label: 'ダッシュボード', end: true },
  { to: '/admin/performers/pending', label: '承認待ち', end: false },
  {
    to: '/admin/performers',
    label: 'パフォーマー',
    end: false,
    excludePrefix: '/admin/performers/pending',
  },
  { to: '/admin/sessions/active', label: '配信中', end: false },
  { to: '/admin/sessions', label: 'セッション履歴', end: true },
  { to: '/admin/audit-logs', label: '監査ログ', end: true },
  { to: '/admin/servers', label: 'サーバー', end: true },
  { to: '/admin/training-data', label: '学習データ', end: true },
];

/**
 * パスから NavLink の active 判定をするピュア関数。
 * テストでも直接呼び出せるよう export する。
 */
export function computeAdminNavActive(currentPath: string, item: AdminNavItem): boolean {
  if (item.end) {
    return currentPath === item.to;
  }
  if (item.excludePrefix !== undefined) {
    if (
      currentPath === item.excludePrefix ||
      currentPath.startsWith(`${item.excludePrefix}/`)
    ) {
      return false;
    }
  }
  return currentPath === item.to || currentPath.startsWith(`${item.to}/`);
}

/**
 * AdminLayout のヘッダ内ナビゲーション。
 * `useLocation` と `computeAdminNavActive` を使い、`end` / `excludePrefix` を
 * 加味した active 判定を行う。
 */
function AdminNav() {
  const { pathname } = useLocation();
  return (
    <nav style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      {NAV_ITEMS.map((item) => {
        const active = computeAdminNavActive(pathname, item);
        // NavLink を使うと標準の prefix-match isActive が `aria-current="page"`
        // を二重設定してしまう(我々が上書き不可能)。回避のため通常の Link
        // に `aria-current` を独自判定で付与する。
        return (
          <Link
            key={item.to}
            to={item.to}
            aria-current={active ? 'page' : undefined}
            style={{
              fontSize: '11px',
              padding: '5px 9px',
              borderRadius: 'var(--radius-md)',
              color: active
                ? 'var(--color-text-primary)'
                : 'var(--color-text-secondary)',
              background: active ? 'rgba(255,255,255,0.04)' : 'transparent',
              textDecoration: 'none',
            }}
          >
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
}

export function AdminLayout({ title, description, actions, children }: AdminLayoutProps) {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();

  async function handleLogout() {
    await logout();
    navigate('/admin/login', { replace: true });
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 24px',
          borderBottom: 'var(--border-default)',
          background: 'var(--color-frame)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <Logo size="sm" />
          <span
            style={{
              fontSize: '10px',
              fontWeight: 500,
              color: 'white',
              background: 'rgba(167, 139, 250, 0.2)',
              border: 'var(--border-accent)',
              padding: '2px 8px',
              borderRadius: '4px',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}
          >
            Admin
          </span>
          <AdminNav />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>
            {admin?.name}
          </span>
          <button
            onClick={handleLogout}
            style={{
              background: 'transparent',
              border: 'var(--border-strong)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-text-secondary)',
              fontSize: '11px',
              padding: '5px 10px',
              cursor: 'pointer',
            }}
          >
            ログアウト
          </button>
        </div>
      </header>

      <main style={{ maxWidth: 1280, margin: '0 auto', padding: '28px 24px 48px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: 16,
            marginBottom: 24,
          }}
        >
          <div>
            <h1 style={{ fontSize: '20px', fontWeight: 500, margin: '0 0 4px' }}>{title}</h1>
            {description && (
              <p style={{ margin: 0, fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                {description}
              </p>
            )}
          </div>
          {actions && <div style={{ flexShrink: 0 }}>{actions}</div>}
        </div>
        {children}
      </main>
    </div>
  );
}
